import React, { useContext, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useParams, useLocation } from 'react-router-dom';
import ROUTES_PATH, { PARAMS } from 'domain/router/routesPathConfig';
import CompanyWorkspaceContext from 'pages/company/WorkspaceRoute/CompanyWorkspaceContext';
import { LayoutCompanyContext } from 'components/mui/Layouts/Company/LayoutCompanyContext';
import RouterNavLink from 'components/mui/Router/RouterNavLink';
import { categoriesTreeSelector, categoryAllSelector, categoryInboxSelector } from 'domain/categories/selectors';
import * as ACL from 'domain/restriction';
import { FormattedMessage } from 'react-intl';
import { identity } from 'effect/Function';

import Categories from 'pages/company/components/CompanyNavigation/Categories';
import FavoritesMenu from 'pages/company/components/CompanyNavigation/FavoritesMenu';
import { DnDItemTarget, DOCUMENT, LINKED, LINKED_DOCUMENT } from 'components/DnDItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import { ListItemIcon } from 'components/mui/Layouts/components/Drawer';
import { ListItemWrapper, ListItemButton } from 'pages/company/components/CompanyNavigation/StyledComponents';
import BetaBadge from 'components/BetaBadge';

import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

type RouteDescriptor = {
  absolute: string;
  restriction: unknown;
};

type NavigationConfigEntry = {
  IconExpanded: React.ElementType;
  IconCollapsed: React.ElementType;
  id: string;
  displayName: string;
  routeConfig: RouteDescriptor;
  defaultChildRouteConfig?: RouteDescriptor;
  activePrefix?: string;
  type: string;
  hasChildren: boolean;
  betaBadge: boolean;
};

type CompanyNavigationProps = {
  onLinkingSidebarClose?: () => void;
  onDocMove?: (...args: unknown[]) => void;
  onRestoreDocuments?: (...args: unknown[]) => void;
  isTrashCategory?: boolean;
};

const DropTarget = DnDItemTarget([DOCUMENT, LINKED, LINKED_DOCUMENT]);

const NAVIGATION_CONFIG: NavigationConfigEntry[] = [
  {
    IconExpanded: DashboardIcon,
    IconCollapsed: DashboardOutlinedIcon,
    id: 'company.dashboard',
    displayName: 'Reports & Analysis',
    routeConfig: ROUTES_PATH.COMPANY_DASHBOARD_VENDOR_EXPENSES,
    type: 'dashboard',
    hasChildren: false,
    betaBadge: true,
  },
  {
    IconExpanded: CurrencyExchangeIcon,
    IconCollapsed: CurrencyExchangeOutlinedIcon,
    id: 'company.bankFeed',
    displayName: 'Bank Feed',
    routeConfig: ROUTES_PATH.COMPANY_BANK_FEED,
    defaultChildRouteConfig: ROUTES_PATH.COMPANY_BANK_FEED_TRANSACTIONS,
    activePrefix: `/company/:${PARAMS.COMPANY_ID}/bank-feed/`,
    type: 'bank-feed',
    hasChildren: false,
    betaBadge: true,
  },
  {
    IconExpanded: ViewCarouselOutlinedIcon,
    IconCollapsed: ViewCarouselOutlinedIcon,
    id: 'company.workspace',
    displayName: 'Workspace',
    routeConfig: ROUTES_PATH.COMPANY_WORKSPACE,
    type: 'standard',
    hasChildren: true,
    betaBadge: false,
  },
  {
    IconExpanded: LockOutlinedIcon,
    IconCollapsed: LockOutlinedIcon,
    id: 'company.confidentialWorkspace',
    displayName: 'Confidential',
    routeConfig: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE,
    type: 'confidential',
    hasChildren: true,
    betaBadge: false,
  },
];

const CompanyNavigation: React.FC<CompanyNavigationProps> = ({
  onLinkingSidebarClose,
  onDocMove,
  onRestoreDocuments,
  isTrashCategory,
}) => {
  const categoriesTree = useSelector(categoriesTreeSelector);
  const restriction = useSelector(ACL.myRestriction);
  const isGranted = useSelector(ACL.isGranted);
  const defaultCategory = useSelector(isGranted(ACL.IS_ACCOUNT) ? categoryInboxSelector : categoryAllSelector);
  const routerParams = useParams();
  const { pathname } = useLocation();

  const { toggle, onToggle } = useContext(LayoutCompanyContext);
  const { companyType } = useContext(CompanyWorkspaceContext);

  const navigationParams = useMemo(
    () => ({
      category1: defaultCategory?.id || '',
      ...routerParams,
    }),
    [defaultCategory?.id, routerParams],
  );

  const createNavigationItems = useCallback((params: Record<string, string | undefined>, currentPath: string) => {
    const isActiveExact = (routePath: string) => generatePath(routePath, params).split('?')[0] === currentPath;
    const isActivePrefix = (prefix: string) => currentPath.startsWith(generatePath(prefix, params));

    return NAVIGATION_CONFIG.map((config) => {
      const linkRouteConfig = config.defaultChildRouteConfig ?? config.routeConfig;
      const isActive = config.activePrefix
        ? isActivePrefix(config.activePrefix)
        : isActiveExact(config.routeConfig.absolute);
      return {
        ...config,
        to: generatePath(linkRouteConfig.absolute, params),
        restriction: config.routeConfig.restriction,
        isActive,
      };
    });
  }, []);

  const navigationsList = useMemo(
    () => createNavigationItems(navigationParams, pathname).filter(ACL.arrayFilter(restriction)),
    [createNavigationItems, navigationParams, pathname, restriction],
  );

  const onDragOver = useCallback(() => {
    if (!toggle) {
      onToggle();
    }
  }, [toggle, onToggle]);

  const handleNavItemClick = useCallback(() => {
    onLinkingSidebarClose?.();
  }, [onLinkingSidebarClose]);

  return (
    <>
      <List
        disablePadding
        sx={{ display: 'block' }}
        component={DropTarget}
        onDragOver={onDragOver}
        onDragLeave={onDragOver}
        onDragEnter={onDragOver}
      >
        {navigationsList.map((nav) => (
          <ListItemWrapper isActive={companyType === nav.type} key={nav.id}>
            <ListItem
              component="div"
              sx={{
                p: 0,
              }}
            >
              <ListItemButton
                component={RouterNavLink}
                href={nav.to}
                selected={nav.isActive}
                onClick={handleNavItemClick}
                isToggle={toggle}
              >
                <ListItemIcon
                  sx={{
                    mr: toggle ? 1 : 'auto',
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <Badge variant="dot" invisible={!(nav.betaBadge && !toggle)} color="warning">
                    {toggle ? <nav.IconExpanded /> : <nav.IconCollapsed />}
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  sx={{ display: toggle ? 'flex' : 'none', whiteSpace: 'normal', fontWeight: 500 }}
                >
                  <FormattedMessage id={nav.id} defaultMessage={nav.displayName} />
                </ListItemText>
                {nav.betaBadge && toggle && <BetaBadge />}
                {nav.hasChildren && (companyType === nav.type ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            {companyType === nav.type && (
              <Categories
                tree={categoriesTree}
                onDocMove={onDocMove}
                onCategoryClick={identity}
                onRestoreDocuments={onRestoreDocuments}
                isTrashCategory={isTrashCategory}
              />
            )}
          </ListItemWrapper>
        ))}
      </List>
      <FavoritesMenu />
    </>
  );
};

export default CompanyNavigation;
